import { createRoutesFromElements, Navigate, Outlet, Route } from "react-router-dom"
import { RequireNoAuth } from "@app/components/redirect/require-no-auth"
import { adminPagesUrl, pagesUrl } from "@app/core/appConstants"
import { withoutAppSideBar } from "@app/core/context/AppSideBarContext/AppSideBarContext"
import RequireIniesChangesOption from "@app/components/redirect/require-inies-changes-option"
import { BSRoutes } from "./BSRouter"
import { BeemetricRoutes } from "./BeemetricRouter"
import { RseeRoutes } from "./RseeRouter"

export const applicationRoutes = createRoutesFromElements(
  <Route element={<Outlet />}>
    {/* Not authenticated user */}
    <Route lazy={() => import("./PublicDashboardLayout").then((module) => ({ Component: module.PublicDashboardLayout }))}>
      <Route
        path={pagesUrl.KAIRNIAL_DASHBOARD}
        lazy={() =>
          import("@app/pages/kairnial/KairnialDashboardPage").then((module) => ({ Component: module.KairnialDashboardPage }))
        }
      />
    </Route>

    <Route
      element={
        <RequireNoAuth>
          <Outlet />
        </RequireNoAuth>
      }>
      <Route
        path="/"
        lazy={() =>
          import("@app/pages/authentication/SignInPage/SignInPage").then((module) => ({ Component: module.default }))
        }
      />
      <Route
        path={pagesUrl.SIGN_IN_PAGE}
        lazy={() =>
          import("@app/pages/authentication/SignInPage/SignInPage").then((module) => ({ Component: module.default }))
        }
      />
      <Route
        path={pagesUrl.SIGN_UP_INVITATION_PAGE}
        lazy={() =>
          import("@app/pages/authentication/SignUpPage/sign-up-page").then((module) => ({ Component: module.SignUpPage }))
        }
      />
      <Route
        path={pagesUrl.FORGOT_PWD_PAGE}
        lazy={() =>
          import("@app/pages/authentication/forgot-password-page/ForgotPasswordPage").then((module) => ({
            Component: module.ForgotPasswordPage,
          }))
        }
      />
      <Route
        path={pagesUrl.CONFIRM_PAGE}
        lazy={() =>
          import("@app/pages/authentication/ConfirmPage/ConfirmPage").then((module) => ({ Component: module.ConfirmPage }))
        }
      />
    </Route>
    {/* End not authenticated user */}

    <Route lazy={() => import("./AuthenticatedLayout").then((module) => ({ Component: module.AuthenticatedLayout }))}>
      <Route
        path={pagesUrl.HOME}
        lazy={() => import("@app/pages/home-page/HomePage").then((module) => ({ Component: module.HomePage }))}
      />

      <Route
        path={pagesUrl.PROJECTS_PAGE}
        lazy={() =>
          import("@app/pages/projects/ProjectsHomePage").then((module) => ({ Component: module.ProjectsHomePage }))
        }
      />

      <Route
        path={pagesUrl.GLOBAL_RSEE_DASHBOARD}
        lazy={() =>
          import("@app/pages/global-rsee-dashboard/GlobalRseeDashboardPage").then((module) => ({
            Component: module.GlobalRseeDashboardPage,
          }))
        }
      />

      <Route
        path={pagesUrl.OPEE_ANALYSIS}
        lazy={() =>
          import("@app/pages/opee-analysis/OpeeAnalysisPage").then((module) => ({ Component: module.OpeeAnalysisPage }))
        }
      />

      <Route
        path={pagesUrl.MY_GAMMES_PAGE}
        lazy={() => import("@app/pages/gammes/MyGammesPage").then((module) => ({ Component: module.MyGammesPage }))}
      />
      <Route
        path={pagesUrl.MY_GAMMES_DETAIL_PAGE}
        lazy={() => import("@app/pages/gammes/GammeDetailPage").then((module) => ({ Component: module.GammeDetailPage }))}
      />
      <Route
        path={pagesUrl.INIES_CHANGES_PAGE}
        lazy={() =>
          import("@app/pages/inies-changes/IniesChangesPage").then((module) => ({
            Component: () => (
              <RequireIniesChangesOption>
                <module.IniesChangesPage />
              </RequireIniesChangesOption>
            ),
          }))
        }
      />

      {RseeRoutes}
      {BSRoutes}
      {BeemetricRoutes}
      <Route lazy={() => import("./ProjectModelLayout").then((module) => ({ Component: module.ProjectModelLayout }))}>
        <Route
          path={pagesUrl.PROJECT_PAGE_DETAIL}
          lazy={() =>
            import("@app/pages/projects/project/ProjectOverviewPage").then((module) => ({
              Component: module.ProjectOverviewPage,
            }))
          }
        />
        <Route lazy={() => import("./BimModelLayout").then((module) => ({ Component: module.BimModelLayout }))}>
          {/* AVA takes the whole screen: no side bar. */}
          <Route
            path={pagesUrl.BIM_MODEL_IMPORT_PAGE}
            handle={withoutAppSideBar}
            lazy={() =>
              import("@app/pages/projects/project/tabs/BimModelTab/BSBimModelImportPage/BSBimModelImportPage").then(
                (module) => ({ Component: module.BSBimModelImportPage })
              )
            }
          />
          <Route
            path={pagesUrl.BIM_MODEL_DETAIL_PAGE}
            lazy={() =>
              import("@app/pages/projects/project/tabs/BimModelTab/BSBimModelDetailPage/BSBimModelDetailPage").then(
                (module) => ({ Component: module.BSBimModelDetailPage })
              )
            }
          />
        </Route>
      </Route>

      {/* User account */}
      <Route
        path={pagesUrl.ACCOUNT_PAGE}
        lazy={() => import("@app/pages/account-page/AccountPage").then((module) => ({ Component: module.AccountPage }))}
      />
      <Route
        path={pagesUrl.MY_OFFER_PAGE}
        lazy={() => import("@app/pages/offer/MyOfferPage").then((module) => ({ Component: module.MyOfferPage }))}
      />
      <Route
        path={pagesUrl.MY_ORGANIZATION_PAGE}
        lazy={() =>
          import("@app/pages/my-organization-page/MyOrganization").then((module) => ({ Component: module.MyOrganization }))
        }
      />
      <Route
        path={pagesUrl.BEEM_SHOT_HELP_CENTER}
        lazy={() =>
          import("@app/pages/support/HelpCenterPage/HelpCenterPage").then((module) => ({
            Component: module.HelperCenterPage,
          }))
        }
      />
      <Route
        path={pagesUrl.HELP_CENTER_TUTORIAL_CHAPTER}
        lazy={() =>
          import("@app/pages/support/TutorialChapterPage/TutorialChapterPage").then((module) => ({
            Component: module.TutorialChapterPage,
          }))
        }
      />
      <Route
        path={pagesUrl.BEEM_SHOT_CONFIGURATION_HELPER}
        lazy={() =>
          import("@app/pages/support/ConfigurationHelperPage/ConfigurationHelperPage").then((module) => ({
            Component: module.ConfigurationHelperPage,
          }))
        }
      />
      <Route
        path={pagesUrl.BEEM_SHOT_TUTORIAL_HELPER}
        lazy={() => import("./TutorialHelperRoute").then((module) => ({ Component: module.TutorialHelperRoute }))}
      />
      {/* End user account */}
    </Route>

    {/* Admin/Ultimate user part */}
    <Route
      path={adminPagesUrl.ADMIN}
      lazy={() => import("./AdminRouteLayout").then((module) => ({ Component: module.AdminRouteLayout }))}>
      <Route index element={<Navigate to={adminPagesUrl.ADMIN_SUMMARY} replace />} />
      <Route
        path={adminPagesUrl.ADMIN_ORGANIZATION_LIST_PAGE}
        lazy={() =>
          import("@app/pages/admin-page/organization/organization-list").then((module) => ({
            Component: module.OrganizationList,
          }))
        }
      />
      <Route
        path={adminPagesUrl.ADMIN_ORGANIZATION_USER_PAGE}
        lazy={() =>
          import("@app/pages/admin-page/user/admin-update-user-form").then((module) => ({
            Component: module.AdminUpdateUserForm,
          }))
        }
      />
      <Route
        path={adminPagesUrl.ADMIN_SUMMARY}
        lazy={() =>
          import("@app/pages/admin-page/statistics/admin-statistics").then((module) => ({
            Component: module.AdminStatistics,
          }))
        }
      />
      <Route
        path={adminPagesUrl.ADMIN_EXPORT_PAGE}
        lazy={() =>
          import("@app/pages/admin-page/exports/admin-exports").then((module) => ({ Component: module.AdminExports }))
        }
      />
      <Route
        path={adminPagesUrl.ADMIN_MACRO_MAKER_PAGE}
        lazy={() =>
          import("@app/pages/admin-page/macro-maker/admin-macro-maker").then((module) => ({
            Component: module.AdminMacroMaker,
          }))
        }
      />
      <Route
        path={adminPagesUrl.ADMIN_JOBS_PAGE}
        lazy={() => import("@app/pages/admin-page/jobs/admin-jobs").then((module) => ({ Component: module.AdminJobs }))}
      />
      <Route
        path={adminPagesUrl.ADMIN_OPEE_PAGE}
        lazy={() => import("@app/pages/admin-page/opee/admin-opee").then((module) => ({ Component: module.AdminOpee }))}
      />

      <Route
        lazy={() =>
          import("./AdminOrganizationRouteLayout").then((module) => ({ Component: module.AdminOrganizationRouteLayout }))
        }>
        {/*Detail of an organization */}
        <Route
          path={adminPagesUrl.ADMIN_ORGANIZATION_PAGE}
          lazy={() =>
            import("@app/pages/admin-page/organization/OrganizationPage").then((module) => ({
              Component: module.OrganizationPage,
            }))
          }
        />

        {/* admin Detail of the users in an organization */}
        <Route
          path={adminPagesUrl.ADMIN_USER_ORGANIZATION_PAGE}
          lazy={() =>
            import("@app/pages/admin-page/organization/user-table-organization-page").then((module) => ({
              Component: module.UserTableOrganizationPage,
            }))
          }
        />
        <Route
          path={adminPagesUrl.ADMIN_ORGANIZATION_AGENCIES_PAGE}
          lazy={() =>
            import("@app/pages/admin-page/organization/AdminAgenciesPage").then((module) => ({
              Component: module.AdminAgenciesPage,
            }))
          }
        />

        <Route
          path={`${adminPagesUrl.ADMIN_ORGANIZATION_INVITE_USER_PAGE}`}
          lazy={() => import("./AdminInviteUserRoute").then((module) => ({ Component: module.AdminInviteUserRoute }))}
        />
        <Route
          path={`${adminPagesUrl.ADMIN_ORGANIZATION_INVITE_FIRST_MEGA_USER}`}
          lazy={() =>
            import("@app/pages/admin-page/user/admin-invite-user-form").then((module) => ({
              Component: module.AdminInviteUserForm,
            }))
          }
        />
        <Route
          path={`${adminPagesUrl.ADMIN_ORGANIZATION_INVITE_FIRST_MEGA_USER}`}
          lazy={() => import("./AdminInviteUserRoute").then((module) => ({ Component: module.AdminInviteUserRoute }))}
        />
      </Route>
    </Route>
    {/* End admin/Ultimate user part*/}
    <Route path="*" lazy={() => import("@app/pages/no-match/no-match").then((module) => ({ Component: module.NoMatch }))} />
  </Route>
)
